import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Container, Typography, Paper, Chip, CircularProgress, Alert, Button,
  TextField, Divider, FormControl, InputLabel, Select, MenuItem, Snackbar,
  ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsIcon from '@mui/icons-material/Directions';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaceIcon from '@mui/icons-material/Place';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import {
  getInvestigation, getActiveTemplate, saveInvestigation, submitInvestigation,
  saveLocalDraft, readLocalDraft, clearLocalDraft,
  pinApplicantLocation, readCurrentPosition, googleMapsDirectionsUrl,
  CiQueueItem, CiQuestionItem,
} from '../services/supabase/ci.service';
import { useAuth } from '../contexts/AuthContext';

const peso = (n?: number | null) =>
  n == null ? '—' : `₱${Number(n).toLocaleString('en-PH', { minimumFractionDigits: 0 })}`;

const CiCapture: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [pinning, setPinning] = useState(false);

  const [item, setItem] = useState<CiQueueItem | null>(null);
  const [questions, setQuestions] = useState<CiQuestionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  const [notes, setNotes] = useState('');
  const [visitDate, setVisitDate] = useState<Date | null>(new Date());
  const [recommendation, setRecommendation] = useState<'approved' | 'disapproved' | ''>('');
  const [employment, setEmployment] = useState<'contractual' | 'regular' | ''>('');
  const [remarks, setRemarks] = useState('');

  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [row, tpl] = await Promise.all([getInvestigation(id), getActiveTemplate()]);
      setItem(row);
      setQuestions(tpl?.items ? [...tpl.items].sort((a, b) => a.order - b.order) : []);

      if (row) {
        const inv = row.investigation;
        setVisitDate(inv.visit_date && isValid(parseISO(inv.visit_date))
          ? parseISO(inv.visit_date) : new Date());
        setRecommendation(inv.recommendation || '');
        setEmployment(inv.employment_observed || '');
        setRemarks(inv.remarks || '');

        // Prefer a newer local draft over what's on the server — the draft
        // exists precisely because a save didn't get through.
        const draft = readLocalDraft(inv.id);
        const serverNotes = inv.field_notes || '';
        if (draft && draft.notes && draft.notes !== serverNotes) {
          setNotes(draft.notes);
          setDraftRestored(true);
        } else {
          setNotes(serverNotes);
        }
      }
    } catch (err: any) {
      console.error('Error loading investigation:', err);
      setError(err.message || 'Failed to load this investigation.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleNotesChange = (value: string) => {
    setNotes(value);
    if (id) saveLocalDraft(id, value);
  };

  /** Chips are prompts, not inputs: tapping one drops the question into the
   *  notes as a heading and puts the cursor under it. */
  const insertQuestion = (q: CiQuestionItem) => {
    const heading = `\n\n${q.label}\n`;
    const next = (notes.trimEnd() + heading).replace(/^\n+/, '');
    handleNotesChange(next);
    setTimeout(() => {
      notesRef.current?.focus();
      const len = next.length;
      notesRef.current?.setSelectionRange(len, len);
    }, 0);
  };

  const persist = async (extra?: { status?: 'in_progress' }) => {
    if (!item) return;
    await saveInvestigation(item.investigation.id, {
      field_notes: notes,
      visit_date: visitDate && isValid(visitDate) ? format(visitDate, 'yyyy-MM-dd') : null,
      recommendation: recommendation || null,
      employment_observed: employment || null,
      remarks: remarks.trim() || null,
      ...(extra?.status ? { status: extra.status } : {}),
    });
  };

  const handlePinHere = async () => {
    if (!item?.applicant || !userProfile) return;
    setPinning(true);
    try {
      const coords = await readCurrentPosition();
      await pinApplicantLocation({
        applicantId: item.applicant.id,
        coords,
        pinnedBy: userProfile.id,
      });
      setSuccess(`Pinned at ${coords.latitude}, ${coords.longitude}`);
      await load();
    } catch (err: any) {
      console.error('Error pinning location:', err);
      setError(err.message || 'Could not pin the location.');
    } finally {
      setPinning(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!item) return;
    setSaving(true);
    try {
      await persist(item.investigation.status === 'assigned' ? { status: 'in_progress' } : undefined);
      clearLocalDraft(item.investigation.id);
      setDraftRestored(false);
      setSuccess('Draft saved.');
      await load();
    } catch (err: any) {
      console.error('Error saving draft:', err);
      setError(err.message || 'Failed to save. Your notes are kept on this device.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!item) return;
    setConfirmOpen(false);
    setSaving(true);
    try {
      await persist();
      await submitInvestigation(item.investigation.id, item.investigation.applicant_id);
      clearLocalDraft(item.investigation.id);
      setSuccess('CI submitted to the office.');
      await load();
    } catch (err: any) {
      console.error('Error submitting CI:', err);
      setError(err.message || 'Failed to submit.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}><CircularProgress /></Box>
      </Container>
    );
  }

  if (!item) {
    return (
      <Container maxWidth="md" sx={{ py: 3, px: { xs: 1.5, sm: 3 } }}>
        <Alert severity="warning">
          This investigation was not found, or it is no longer assigned to you.
        </Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} onClick={() => navigate('/ci-queue')}>
          Back to queue
        </Button>
      </Container>
    );
  }

  const { investigation: inv, applicant } = item;
  const submitted = inv.status === 'submitted';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container
        maxWidth="md"
        sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}
      >
        <Button
          size="small" startIcon={<ArrowBackIcon />} sx={{ mb: 1 }}
          onClick={() => navigate('/ci-queue')}
        >
          Queue
        </Button>

        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700 }}>
          {applicant?.full_name || 'Applicant'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {applicant?.current_address || 'No address on file'}
        </Typography>

        {/* Context the officer needs while standing in the barangay */}
        <Paper
          variant="outlined"
          sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Requested</Typography>
              <Typography sx={{ fontWeight: 700 }}>{peso(applicant?.requested_amount)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Product</Typography>
              <Typography sx={{ fontWeight: 700 }}>{applicant?.product_name || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Claimed employer</Typography>
              <Typography sx={{ fontWeight: 700 }}>{applicant?.employer_name || '—'}</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Finding the house, and recording exactly where it is */}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <PlaceIcon fontSize="small" color="action" />
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Location</Typography>
            {applicant?.latitude != null && applicant?.longitude != null ? (
              <Chip
                size="small" color="success" variant="outlined"
                label={`${Number(applicant.latitude).toFixed(5)}, ${Number(applicant.longitude).toFixed(5)}`}
              />
            ) : (
              <Chip size="small" label="Not pinned yet" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {googleMapsDirectionsUrl(applicant) && (
              <Button
                size="small" variant="outlined" startIcon={<DirectionsIcon />}
                component="a" href={googleMapsDirectionsUrl(applicant) as string}
                target="_blank" rel="noopener noreferrer"
                sx={{ minHeight: 40 }}
              >
                Directions
              </Button>
            )}
            <Button
              size="small" variant="contained" startIcon={<MyLocationIcon />}
              onClick={handlePinHere} disabled={pinning || submitted}
              sx={{ minHeight: 40 }}
            >
              {pinning ? 'Pinning…' : 'Pin this location'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Tap "Pin this location" while standing at the applicant's house — it saves the exact
            spot for future visits and collections.
          </Typography>
        </Paper>

        {submitted && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Submitted{inv.submitted_at && isValid(parseISO(inv.submitted_at))
              ? ` on ${format(parseISO(inv.submitted_at), 'MMM dd, yyyy')}` : ''}. It's now with the office.
          </Alert>
        )}
        {draftRestored && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDraftRestored(false)}>
            Restored unsaved notes from this device. Save the draft to sync them.
          </Alert>
        )}

        {/* Question chips */}
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
          Checklist prompts — tap to add to your notes
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, mb: 2 }}>
          {questions.map((q) => {
            const used = notes.includes(q.label);
            return (
              <Tooltip key={q.key} title={q.label_en} arrow>
                <Chip
                  label={`${q.order}. ${q.label}`}
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => insertQuestion(q)}
                  disabled={submitted}
                  color={used ? 'success' : q.asks === 'neighbor' ? 'primary' : 'default'}
                  variant={used ? 'filled' : 'outlined'}
                  sx={{ height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                />
              </Tooltip>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Blue prompts are asked of the <strong>neighbours</strong> about the applicant. Write freely
          in Bisaya, Tagalog or English — the office sorts it out.
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={isMobile ? 10 : 14}
          label="Field notes"
          placeholder="Everything you learned on the visit…"
          value={notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          disabled={submitted}
          inputRef={notesRef}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
          Your assessment
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5, mb: 2 }}>
          <DatePicker
            label="CI date"
            value={visitDate}
            onChange={setVisitDate}
            disabled={submitted}
            sx={{ minWidth: isMobile ? '100%' : 200 }}
          />
          <FormControl sx={{ minWidth: isMobile ? '100%' : 220 }}>
            <InputLabel>Employment observed</InputLabel>
            <Select
              value={employment}
              label="Employment observed"
              onChange={(e) => setEmployment(e.target.value as any)}
              disabled={submitted}
            >
              <MenuItem value="regular">Regular</MenuItem>
              <MenuItem value="contractual">Contractual</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Recommendation
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={recommendation}
          onChange={(_, v) => { if (v !== null) setRecommendation(v); }}
          disabled={submitted}
          fullWidth={isMobile}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="approved" color="success" sx={{ minHeight: 44, px: 3 }}>
            Approve
          </ToggleButton>
          <ToggleButton value="disapproved" color="error" sx={{ minHeight: 44, px: 3 }}>
            Disapprove
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Remarks (if any)"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          disabled={submitted}
          sx={{ mb: 3 }}
        />

        {!submitted && (
          <Box sx={{ display: 'flex', gap: 1.5, flexDirection: isMobile ? 'column' : 'row', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined" onClick={handleSaveDraft} disabled={saving}
              fullWidth={isMobile} sx={{ minHeight: 44 }}
            >
              {saving ? <CircularProgress size={22} /> : 'Save draft'}
            </Button>
            <Button
              variant="contained" onClick={() => setConfirmOpen(true)}
              disabled={saving || !recommendation}
              fullWidth={isMobile} sx={{ minHeight: 44 }}
            >
              Submit CI
            </Button>
          </Box>
        )}
        {!submitted && !recommendation && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
            Choose Approve or Disapprove before submitting.
          </Typography>
        )}

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Submit this CI?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Once submitted, the office reviews it and you can no longer edit your notes.
              Save a draft instead if you still need to add anything.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">Submit</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={!!error} autoHideDuration={8000} onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default CiCapture;
